import React, { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearAuthError,
  selectAuthLoading,
  selectAuthError,
} from "../../redux/slices/authSlice";

export default function LoginForm({
  email,
  setEmail,
  password,
  setPassword,
  onSubmit,
  onSignupClick,
  onForgotPasswordClick,
}) {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);

  const alertRef = useRef(null);
  const emailRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (error && alertRef.current) {
      alertRef.current.focus();
    }
  }, [error]);

  return (
    <>
      <div className="login-header">
        <div className="header-title">Login</div>
      </div>

      <form className="login-form" onSubmit={onSubmit}>
        <label className="login-field-label">
          Email <span className="required-asterisk" aria-hidden="true">*</span>
        </label>
        <input
          ref={emailRef}
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={isLoading}
        />
        <label className="login-field-label">
          Password <span className="required-asterisk" aria-hidden="true">*</span>
        </label>
        <div className="password-input-wrap">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
          />
          <button
            type="button"
            className="password-toggle-btn"
            onClick={() => setShowPassword((value) => !value)}
            disabled={isLoading}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <svg
                className="password-toggle-icon"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 3l18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M10.58 10.58a2 2 0 1 0 2.83 2.83"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9.88 5.09A10.94 10.94 0 0 1 12 4.9c7 0 10.5 7.1 10.5 7.1a16.4 16.4 0 0 1-4.16 4.86M6.61 6.61A16.57 16.57 0 0 0 1.5 12s3.5 7.1 10.5 7.1c1.53 0 2.94-.33 4.2-.9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg
                className="password-toggle-icon"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M1.5 12s3.5-7.1 10.5-7.1S22.5 12 22.5 12 19 19.1 12 19.1 1.5 12 1.5 12z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="3"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            )}
          </button>
        </div>

        {error && (
          <div
            ref={alertRef}
            tabIndex={-1}
            className={`alert-banner ${/pending|pending approval/i.test(error) ? "alert-warning" : "alert-error"}`}
            role="alert"
            aria-live="assertive"
          >
            <div className="alert-icon" aria-hidden>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 9v4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 17h.01"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                  stroke="currentColor"
                  strokeWidth="0"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div className="alert-body">
              <div className="alert-text">{error}</div>
            </div>
            <button
              className="alert-dismiss"
              aria-label="Dismiss"
              onClick={() => dispatch(clearAuthError())}
            >
              ×
            </button>
          </div>
        )}

        <button className="submit" type="submit" disabled={isLoading}>
          {isLoading ? "Signing in..." : "Sign in"}
        </button>

        <button
          type="button"
          className="text-action-btn"
          onClick={onForgotPasswordClick}
          disabled={isLoading}
        >
          Forgot password?
        </button>

        <div className="signup-link">
          Don't have an account?{" "}
          <a href="/signup" onClick={onSignupClick}>
            Sign Up
          </a>
        </div>
      </form>
    </>
  );
}
